import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import { createClientId } from "../lib/ids";

export type ToastOptions = {
  title: string;
  tone?: "success" | "error" | "neutral";
  actionLabel?: string;
  onAction?: () => void | Promise<void>;
};

type ToastItem = ToastOptions & {
  id: string;
};

type ToastContextValue = {
  showToast: (toast: ToastOptions) => string;
  dismissToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (toast: ToastOptions) => {
      const id = createClientId("toast");
      setToasts((current) => [...current, { ...toast, id }]);
      window.setTimeout(() => dismissToast(id), 6000);
      return id;
    },
    [dismissToast]
  );

  const value = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" role="status" aria-live="polite" aria-label="Notifications">
        {toasts.map((toast) => (
          <div className={`toast toast--${toast.tone ?? "success"}`} key={toast.id}>
            <span>{toast.title}</span>
            {toast.actionLabel && toast.onAction ? (
              <button
                className="toast__action"
                type="button"
                onClick={() => {
                  void toast.onAction?.();
                  dismissToast(toast.id);
                }}
              >
                {toast.actionLabel}
              </button>
            ) : null}
            <button className="icon-button icon-button--ghost" type="button" aria-label={t("common.close")} onClick={() => dismissToast(toast.id)}>
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) {
    throw new Error("useToast must be used inside ToastProvider");
  }
  return value;
}
